import { ArrowRight, Bitcoin, CircleDollarSign, Coins, Cpu, Hexagon, Zap } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { useSmoothScroll } from "./SmoothScroll";

const PRODUCTS = [
  { icon: Cpu, name: "Winter AI Engine", apr: "40%", tag: "Flagship AI engine" },
  { icon: Coins, name: "Gold Growth", apr: "15%", tag: "Gold-focused strategy" },
  { icon: Bitcoin, name: "Bitcoin Growth", apr: "25%", tag: "BTC-focused strategy" },
  { icon: Hexagon, name: "Ethereum Growth", apr: "25%", tag: "ETH-focused strategy" },
  { icon: CircleDollarSign, name: "Silver Growth", apr: "25%", tag: "Silver-focused strategy" },
  { icon: Zap, name: "Alpha AI Strategy", apr: "200%", tag: "High-octane AI strategy", featured: true },
];

export default function AITrading() {
  const scrollTo = useSmoothScroll();

  return (
    <section id="ai-trading" className="relative py-24 sm:py-32 border-t border-white/[0.06]" data-testid="ai-trading-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_15%,rgba(212,175,55,0.06),transparent_55%)]" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          chapter="Chapter 02"
          kicker="Algorithmic Capital"
          title="AI-Powered Investment Products"
          subtitle="Dedicated AI strategies designed to participate in digital market opportunities."
        />

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}>
              <div
                className={`group relative h-full overflow-hidden rounded-2xl border p-8 backdrop-blur-xl transition-all duration-500 ${
                  p.featured
                    ? "border-[#D4AF37]/50 bg-[#14141A]/90 shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_70px_rgba(212,175,55,0.1)]"
                    : "border-white/10 bg-[#14141A]/80 shadow-[0_8px_32px_rgba(0,0,0,0.5)] hover:border-[#D4AF37]/30 hover:bg-[#1A1A22]/80"
                }`}
                data-testid={`ai-product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {p.featured && (
                  <span className="absolute right-5 top-5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 font-mono text-[9px] tracking-[0.2em] text-[#F3E5AB] uppercase">
                    Flagship
                  </span>
                )}
                <div className={`rounded-xl border p-3 w-fit transition-colors duration-500 ${
                  p.featured
                    ? "border-[#D4AF37]/40 bg-[#D4AF37]/15 text-[#F3E5AB]"
                    : "border-[#D4AF37]/25 bg-[#D4AF37]/[0.07] text-[#D4AF37] group-hover:bg-[#D4AF37]/15"
                }`}>
                  <p.icon className="h-5 w-5" />
                </div>

                <h3 className="mt-6 font-display text-xl font-semibold text-white">{p.name}</h3>
                <p className="mt-1 text-sm text-[#636B78]">{p.tag}</p>

                <div className="mt-8 flex items-end gap-2">
                  <span className={`font-display text-5xl font-extrabold tracking-tight ${p.featured ? "text-gold-gradient" : "text-white"}`}>
                    {p.apr}
                  </span>
                </div>
                <p className="mt-2 font-mono text-[10px] tracking-[0.28em] text-[#99A1AF] uppercase">
                  Estimated APR
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mx-auto mt-10 max-w-2xl text-center text-[11px] leading-relaxed text-[#636B78]" data-testid="ai-apr-disclaimer">
            All figures shown are estimated APR based on strategy design and market assumptions.
            They are illustrative and do not represent guaranteed returns.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-8 text-center">
            <button
              onClick={() => scrollTo?.("#download")}
              data-testid="ai-explore-products-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-[#08080A] transition-transform duration-300 hover:scale-[1.04]"
            >
              Explore AI Products
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
